export type EventSort = "newest" | "oldest" | "name-asc" | "name-desc";

export const EVENT_SORTS: readonly { value: EventSort; label: string; hint: string }[] = [
  { value: "newest", label: "Newest", hint: "Newest first (date created)" },
  { value: "oldest", label: "Oldest", hint: "Oldest first (date created)" },
  { value: "name-asc", label: "A–Z", hint: "Name, A to Z" },
  { value: "name-desc", label: "Z–A", hint: "Name, Z to A" },
];

export function isEventSort(value: unknown): value is EventSort {
  return EVENT_SORTS.some((option) => option.value === value);
}

interface Sortable {
  readonly id: string;
  readonly name: string;
  /** ISO timestamp from the API; absent on very old rows. */
  readonly createdAt?: string | null;
}

function created(event: Sortable): number {
  const time = event.createdAt ? Date.parse(event.createdAt) : Number.NaN;
  return Number.isFinite(time) ? time : 0;
}

/** Name order ignores case and accents, and numbers sort by value ("Event 2" before "Event 10"). */
const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

/** A new array; never reorders the one it is given. Ties fall back to name, then id, so the order is stable. */
export function sortEvents<T extends Sortable>(events: readonly T[], sort: EventSort): T[] {
  const byName = (a: T, b: T): number =>
    collator.compare(a.name, b.name) || a.id.localeCompare(b.id);
  return events.slice().sort((a, b) => {
    switch (sort) {
      case "newest":
        return created(b) - created(a) || byName(a, b);
      case "oldest":
        return created(a) - created(b) || byName(a, b);
      case "name-asc":
        return byName(a, b);
      case "name-desc":
        return byName(b, a);
    }
  });
}
